import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Search, Tag, Calendar, User, ChevronRight, AlertCircle, MessageSquare } from 'lucide-react';
import Button from '../components/ui/Button';
import { CREATE_MAILING_LIST_SUBSCRIPTION } from '../graphql/mutations/createMailingListSubscription';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  slug: string;
  author: {
    name: string;
    avatar: string;
  };
  date: string;
  category: string;
  imageUrl: string;
  readTime: number;
  featured?: boolean;
}

const mockPosts: BlogPost[] = [
  {
    id: '1',
    title: '10 essentielle værktøj enhver husejer bør have',
    excerpt: 'En omfattende guide til at opbygge din grundlæggende værktøjssamling til vedligeholdelse og reparationer i hjemmet.',
    content: '',
    slug: '10-essential-tools-every-homeowner-should-have',
    author: {
      name: 'MoreBot',
      avatar: '/images/us/cto.png',
    },
    date: '2026-02-12',
    category: 'Hjemmeforbedring',
    imageUrl: '/images/blogPosts/blog_post_1.png',
    readTime: 2,
    featured: true,
  },
  {
    id: '2',
    title: 'Sådan vedligeholder du dit el-værktøj korrekt',
    excerpt: 'Lær de bedste fremgangsmåder for at holde dit el-værktøj i topstand i mange år.',
    content: '',
    slug: 'how-to-properly-maintain-your-power-tools',
    author: {
      name: 'MoreBot',
      avatar: '/images/us/cto.png',
    },
    date: '2026-02-12',
    category: 'Værktøjsvedligeholdelse',
    imageUrl: '/images/blogPosts/blog_post_3.png',
    readTime: 3,
  },
  {
    id: '3',
    title: 'Opbygning af fællesskab gennem værktøjsdeling',
    excerpt: 'Oplev hvordan værktøjsdelingsplatforme bringer nabolag sammen og fremmer fællesskabsfølelse.',
    content: '',
    slug: 'building-community-through-tool-sharing',
    author: {
      name: 'MoreBot',
      avatar: '/images/us/cto.png',
    },
    date: '2026-02-12',
    category: 'Fællesskab',
    imageUrl: '/images/blogPosts/blog_post_2.png',
    readTime: 4,
    featured: true,
  },
];

const categories = [
  'Hjemmeforbedring',
  'Værktøjsvedligeholdelse',
  'Gør-det-selv projekter',
  'Fællesskab',
  'Bæredygtighed',
  'Tips & tricks',
];

const Blog: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterError, setNewsletterError] = useState('');
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  const [createMailingListSubscription, { loading: isSubscribing }] = useMutation(CREATE_MAILING_LIST_SUBSCRIPTION);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterError('');
    setNewsletterSuccess(false);

    if (!validateEmail(newsletterEmail)) {
      setNewsletterError('Indtast venligst en gyldig email-adresse.');
      return;
    }

    try {
      const { data } = await createMailingListSubscription({
        variables: {
          input: {
            email: newsletterEmail,
          },
        },
      });

      if (data?.createMailingListSubscription?.mailingListSubscription) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
      } else if (data?.createMailingListSubscription?.errors) {
        setNewsletterError(data.createMailingListSubscription.errors);
      } else {
        setNewsletterError('Kunne ikke tilmelde nyhedsbrev. Pr\u00f8v venligst igen.');
      }
    } catch (err) {
      setNewsletterError('Kunne ikke tilmelde nyhedsbrev. Pr\u00f8v venligst igen.');
    }
  };

  const featuredPosts = mockPosts.filter(post => post.featured);
  const regularPosts = mockPosts.filter(post => !post.featured);

  const filteredPosts = mockPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">DoMore Blog</h1>
          <p className="text-xl text-gray-600">
            Tips, historier og indsigt om værktøjsdeling og fællesskabsopbygning
          </p>
        </div>

        {/* Search and Filters */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-grow">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Søg artikler..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value || null)}
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
              >
                <option value="">Alle kategorier</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Featured Posts */}
        {!searchQuery && !selectedCategory && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Fremhævede artikler</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {featuredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <Tag className="h-4 w-4 mr-1" />
                      <span>{post.category}</span>
                      <span className="mx-2">•</span>
                      <span>{post.readTime} min læsning</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-green-600 mb-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{post.excerpt}</p>
                    <div className="flex items-center">
                      <img
                        src={post.author.avatar}
                        alt={post.author.name}
                        className="h-8 w-8 rounded-full mr-2 object-cover"
                      />
                      <div className="text-sm">
                        <p className="text-gray-900 font-medium">{post.author.name}</p>
                        <p className="text-gray-500">{formatDate(post.date)}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* All Posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(searchQuery || selectedCategory ? filteredPosts : regularPosts).map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
            >
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={post.imageUrl}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Tag className="h-4 w-4 mr-1" />
                  <span>{post.category}</span>
                  <span className="mx-2">•</span>
                  <span>{post.readTime} min læsning</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-green-600 mb-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <img
                      src={post.author.avatar}
                      alt={post.author.name}
                      className="h-8 w-8 rounded-full mr-2 object-cover"
                    />
                    <div className="text-sm">
                      <p className="text-gray-900 font-medium">{post.author.name}</p>
                      <p className="text-gray-500">{formatDate(post.date)}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-green-600" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 bg-green-600 rounded-lg shadow-sm text-white p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Hold dig opdateret</h2>
          <p className="text-green-100 mb-6">
            Få de nyeste artikler, tips og fællesskabshistorier leveret til din indbakke.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
            <div className="flex gap-4 mb-4">
              <input
                type="email"
                placeholder="Indtast din email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-grow px-4 py-2 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-green-600"
                required
              />
              <button
                type="submit"
                disabled={isSubscribing}
                className="px-4 py-2 bg-white text-green-600 font-medium rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-green-600 whitespace-nowrap disabled:opacity-50"
              >
                {isSubscribing ? 'Tilmelder...' : 'Tilmeld'}
              </button>
            </div>

            {newsletterError && (
              <div className="rounded-md bg-red-50 p-3 text-left">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{newsletterError}</p>
                  </div>
                </div>
              </div>
            )}

            {newsletterSuccess && (
              <div className="rounded-md bg-white p-3 text-left">
                <div className="flex">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm text-green-900">
                      Tak for din tilmelding! Du vil snart modtage vores nyhedsbrev.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Blog;